import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { LanguageContextProvider } from '@wix/ai-scheduling-assistant-data-store';
import { Messages, UouChatMessages } from '@wix/ai-scheduling-assistant-localization';
import { DeviceTypeContextProvider } from '@wix/ai-scheduling-assistant-view-react';
import { TranslationsProvider as WidgetTranslationsProvider, WixLocale } from '@wix/chat-widget-ui-lib';
import { WixDesignSystemProvider } from '@wix/design-system';
import { create as createFedopsLogger } from '@wix/fedops-logger';
import { UouChatTranslator } from './client/UouChatTranslator';
import { ErrorPage } from './components/ErrorPage';
import { ParentPostMessagePage } from './components/ParentPostMessagePage';
import { SessionExpiredPage } from './components/SessionExpiredPage';
import { UnauthorizedPage } from './components/UnauthorizedPage';
import { UoUChat } from './components/uou-chat/components/UoUChat';
import { AuthTokenContextProvider } from './context/AuthTokenContext';
import { UouLanguageContextProvider } from './context/UouLanguageContext';
import { MessageType } from './hooks/useWindowPostMessage';
import { WithHttpClientsRouteWrapper } from './route/WithHttpClientsRouteWrapper';
import './client.scss';
import '@wix/design-system/styles.global.css';

const locale = window.__LOCALE__;

const fedopsLogger = createFedopsLogger('ai-scheduling-assistant-standalone-client');

// Move the following `appLoaded()` call to the point where your app has fully loaded.
// See https://github.com/wix-private/fed-infra/blob/master/fedops/fedops-logger/README.md
fedopsLogger.appLoaded();

const domNode = document.getElementById('root')!;
const root = createRoot(domNode);

const AppRoutes = () => {
  return (
    <BrowserRouter future={{ v7_relativeSplatPath: true }}>
      <Routes>
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
        <Route path="/session-expired" element={<SessionExpiredPage />} />
        <Route path="/error" element={<ErrorPage />} />
        <Route path="/:brand?/connect/success" element={<ParentPostMessagePage messageType={MessageType.AUTH_SUCCESS} text="Success" />} />
        <Route path="/:brand?/connect/failure" element={<ParentPostMessagePage messageType={MessageType.AUTH_FAILURE} text="Failure" />} />
        <Route path="/:brand?/payment-success" element={<ParentPostMessagePage messageType={MessageType.PAYMENT_SUCCESS} text="Payment Success" />} />

        <Route
          path="individuals/:metaSiteId"
          element={
            <WithHttpClientsRouteWrapper useIndividualAssistant>
              <UoUChat useIndividualAssistant />
            </WithHttpClientsRouteWrapper>
          }
        />

        <Route path="/:metaSiteId">
          <Route
            index
            element={
              <WithHttpClientsRouteWrapper>
                <UoUChat />
              </WithHttpClientsRouteWrapper>
            }
          />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

async function init() {
  const translator = new UouChatTranslator();
  await translator.initialize(locale);
  const uouLanguage = new UouChatMessages(translator);
  const language = new Messages(translator);

  root.render(
    <Suspense fallback="...loading">
      <WixDesignSystemProvider features={{ newColorsBranding: true }}>
        <I18nextProvider i18n={translator.getI18n()}>
          <LanguageContextProvider language={language}>
            <UouLanguageContextProvider language={uouLanguage}>
              <WidgetTranslationsProvider locale={locale as WixLocale}>
                <AuthTokenContextProvider>
                  <DeviceTypeContextProvider>
                    <AppRoutes />
                  </DeviceTypeContextProvider>
                </AuthTokenContextProvider>
              </WidgetTranslationsProvider>
            </UouLanguageContextProvider>
          </LanguageContextProvider>
        </I18nextProvider>
      </WixDesignSystemProvider>
    </Suspense>,
  );
}

init();
